import { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { setUser, clearAuth } from '@/stores/authSlice'
import { authService } from '@/services/api'
import LoadingScreen from '@/components/LoadingScreen'

import LandingPage from './pages/Landing'
import LoginPage from './pages/Login'
import SignupPage from './pages/Signup'
import ChatPage from './pages/Chat'
import CallsPage from './pages/Calls'
import SettingsPage from './pages/Settings'
import AdminPage from './pages/Admin'
import ProfilePage from './pages/Profile'
import FriendsPage from './pages/Friends'
import AuthCallback from './pages/AuthCallback'
import BotDirectoryPage from './pages/BotDirectory'
import ProtectedRoute from '@/components/ProtectedRoute'

function App() {
  const dispatch = useAppDispatch()
  const { isAuthenticated, isLoading } = useAppSelector((state) => state.auth)

  useEffect(() => {
    // Apply saved appearance prefs (theme, font size, density, streamer mode)
    try {
      const prefs = JSON.parse(localStorage.getItem('aethex_appearance') || '{}');
      const root = document.documentElement;
      if (prefs.theme)     root.setAttribute('data-theme', prefs.theme);
      if (prefs.fontSize)  root.style.setProperty('--chat-font-size', `${prefs.fontSize}px`);
      if (prefs.density)   root.setAttribute('data-density', prefs.density);
      if (prefs.streamer)  root.setAttribute('data-streamer', 'on');
      if (prefs.saturation && prefs.saturation !== 100) root.style.filter = `saturate(${prefs.saturation / 100})`;
      if (prefs.reducedMotion) root.style.setProperty('--transition-speed', '0ms');
    } catch (_) {}
  }, [])

  useEffect(() => {
    // Skip during SSO callback — AuthCallback handles the token
    if (window.location.pathname.startsWith('/auth/callback')) return
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token')
      if (!token) return
      try {
        const response = await authService.getMe()
        const userData = response.data?.data ?? response.data
        dispatch(setUser(userData))
      } catch {
        // Use clearAuth (not logout) so we don't nuke localStorage here.
        // AeThexProvider runs its own /auth/me check concurrently and will
        // call localStorage.removeItem + navigate('/login') if the token is
        // truly invalid. This avoids a race where Redux clears localStorage
        // before AeThexProvider finishes its own check.
        dispatch(clearAuth())
      }
    }
    checkAuth()
  }, [dispatch])

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/app" /> : <LoginPage />}
        />
        <Route
          path="/signup"
          element={isAuthenticated ? <Navigate to="/app" /> : <SignupPage />}
        />

        {/* SSO Callback */}
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Protected Routes */}
        <Route
          path="/app"
          element={<ProtectedRoute element={<ChatPage />} />}
        />
        <Route
          path="/calls"
          element={<ProtectedRoute element={<CallsPage />} />}
        />
        <Route
          path="/settings"
          element={<ProtectedRoute element={<SettingsPage />} />}
        />
        <Route
          path="/friends"
          element={<ProtectedRoute element={<FriendsPage />} />}
        />
        <Route
          path="/profile/:userId?"
          element={<ProtectedRoute element={<ProfilePage />} />}
        />
        <Route
          path="/admin"
          element={<ProtectedRoute element={<AdminPage />} />}
        />
        <Route
          path="/bots"
          element={<ProtectedRoute element={<BotDirectoryPage />} />}
        />
        {/* Root — landing for guests, app for authenticated */}
        <Route path="/" element={isAuthenticated ? <Navigate to="/app" /> : <LandingPage />} />
      </Routes>
    </Router>
  )
}

export default App
